/**
 * ESCHER FALLS — App Store の評価をいつ頼むか。
 *
 * ## なぜ入れたか
 * 評価が 0 件だと、検索でも上がらず、ページに来た人にも入れてもらえない。
 * 公開しただけでは誰も書かない。**遊んでいる最中に、その場で頼むしかない。**
 *
 * ## 頼む場所の決め方
 * 出すか出さないかを最後に決めるのは OS で、年に3回までという上限がある。
 * 超えた呼び出しは黙って捨てられるので、**回数を使う場所を選ぶ**のが仕事になる。
 *
 * 規則:
 * 1. **通水した直後にしか頼まない。** 失敗の後に頼むのは、低い星を集めに行くのと同じ。
 * 2. **本編を数面通したとき**に一度（面数は `main.ts` の `REVIEW_AFTER_MAIN_CLEARS`）。
 *    まだ課金の話（第一章末＝12面）に触れていない。**売り込みの隣で頼まない。**
 * 3. **本編を全部通したとき**にもう一度。ここがいちばん機嫌の良い瞬間。
 *    ただし祝いの画面とは重ねない（重ねると祝いが読めない）。
 * 4. 一度使った機会は二度と使わない。端末に残す。
 *
 * ## 押させてはいけない
 * 「評価する」ボタンからこの API を呼ぶのは **Apple が明確に禁じている。**
 * ボタンを置くなら `itms-apps://` を開く方であって、これではない。
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

interface ReviewNative {
  request(): Promise<{ shown: boolean }>;
}

const Review = registerPlugin<ReviewNative>('Review');

/**
 * 頼む機会。使ったものは記録して二度と使わない。
 *
 * **2026-09-23 に `eightCleared` を `earlyCleared` に改めた。**
 * 8面固定をやめて面数を `main.ts` 側の定数にしたので、名前が嘘になったため。
 * 名前が変わると、**既に8面版で一度断った人にもう一度だけ訊くことになる。**
 * 評価0件のいまはそれでよい（OS 側に年3回の上限があるので、しつこくはならない）。
 */
export type ReviewMoment = 'earlyCleared' | 'mainComplete';

const KEY = 'escherfalls.review.asked.v1';

/**
 * 使った機会を読む。
 *
 * **進行状況（`records.ts`）とは分けてある。** これは遊びの記録ではなく
 * 「何回頼んだか」の控えで、消えても遊びは壊れない。
 * 同じ入れ物に混ぜると、記録の書式を変えるたびに巻き添えになる。
 */
function asked(): Set<string> {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function remember(moment: ReviewMoment): void {
  try {
    const next = asked();
    next.add(moment);
    window.localStorage.setItem(KEY, JSON.stringify([...next]));
  } catch {
    // 保存できない環境（プライベートモードなど）。頼みすぎるより黙るほうがましだが、
    // OS 側にも上限があるので放っておいてよい。
  }
}

/** その機会をもう使ったか。 */
export function alreadyAsked(moment: ReviewMoment): boolean {
  return asked().has(moment);
}

/**
 * 評価を頼む。**使えるのは一機会につき一度きり。**
 *
 * 失敗しても投げない。頼めなかっただけで、遊びは何も変わらない。
 */
export async function requestReview(moment: ReviewMoment): Promise<void> {
  if (alreadyAsked(moment)) return;
  if (!Capacitor.isNativePlatform()) return;
  // **先に控える。** 呼んだあとに控えようとすると、途中で落ちたときに
  // 何度も頼むことになる。頼めなかった一回を惜しむより、しつこくない方を取る。
  remember(moment);
  try {
    await Review.request();
  } catch {
    // プラグインが繋がっていない。売り場と違って、黙って諦めてよい。
  }
}
